/**
 * Яндекс.Метрика — runtime-опциональная интеграция.
 *
 * Пустой VITE_YM_COUNTER_ID → все функции no-op.
 * SPA pageview шлём вручную через trackPageview на смене route.
 * Дополнительно можно включить глобальный click tracking по всем интерактивным
 * элементам; он добирает "хвосты" без ручной разметки каждого onClick.
 */

import { getPwaContext } from './features/pwa/platform'

type YmFn = {
  (counterId: number, method: 'init', options?: Record<string, unknown>): void
  (counterId: number, method: 'hit', url: string, options?: Record<string, unknown>): void
  (counterId: number, method: 'reachGoal', target: string, params?: Record<string, unknown>): void
  (counterId: number, method: 'setUserID', userId: string): void
  (counterId: number, method: 'userParams', params: Record<string, unknown>): void
  (counterId: number, method: 'params', payload: { params: Record<string, unknown> }): void
  (counterId: number, method: string, ...args: unknown[]): void
  a?: unknown[][]
  l?: number
}

declare global {
  interface Window {
    ym?: YmFn
  }
}

const COUNTER_ID_RAW = import.meta.env.VITE_YM_COUNTER_ID
const COUNTER_ID = COUNTER_ID_RAW ? Number(COUNTER_ID_RAW) : 0
const WEBVISOR = import.meta.env.VITE_YM_WEBVISOR === 'true'
const TRACK_ALL_CLICKS = import.meta.env.VITE_YM_TRACK_ALL_CLICKS !== 'false'
const SCRIPT_SRC = 'https://mc.yandex.ru/metrika/tag.js'
const CLICK_SELECTOR = [
  '[data-ym-click]',
  'a[href]',
  'button',
  '[role="button"]',
  'input[type="button"]',
  'input[type="submit"]',
  'input[type="reset"]',
  'summary',
].join(',')

const isEnabled = (): boolean => COUNTER_ID > 0

let initialized = false

function compactParams(params: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== ''),
  )
}

function cleanText(value: string | null | undefined, max = 100): string | undefined {
  if (!value) return undefined
  const normalized = value.replace(/\s+/g, ' ').trim()
  if (!normalized) return undefined
  return normalized.slice(0, max)
}

function normalizeHref(rawHref: string | null): string | undefined {
  const href = cleanText(rawHref, 300)
  if (!href || typeof window === 'undefined') return href

  try {
    const url = new URL(href, window.location.origin)
    if (url.origin === window.location.origin) {
      return `${url.pathname}${url.search}${url.hash}`
    }
    return url.href
  } catch {
    return href
  }
}

function getElementLabel(element: HTMLElement): string | undefined {
  const explicit = cleanText(element.getAttribute('data-ym-click'))
  if (explicit) return explicit

  const ariaLabel = cleanText(element.getAttribute('aria-label'))
  if (ariaLabel) return ariaLabel

  const title = cleanText(element.getAttribute('title'))
  if (title) return title

  if (element instanceof HTMLInputElement) {
    return cleanText(element.value) ?? cleanText(element.placeholder)
  }

  if (element instanceof HTMLImageElement) {
    const alt = cleanText(element.alt)
    if (alt) return alt
  }

  return cleanText(element.innerText || element.textContent)
}

function extractClickPayload(element: HTMLElement): Record<string, unknown> {
  const tag = element.tagName.toLowerCase()
  const href = element instanceof HTMLAnchorElement
    ? normalizeHref(element.getAttribute('href'))
    : undefined
  const label = getElementLabel(element)
  const inputType = element instanceof HTMLInputElement ? cleanText(element.type, 40) : undefined
  const role = cleanText(element.getAttribute('role'), 40)
  const className = cleanText(Array.from(element.classList).slice(0, 4).join(' '), 120)
  const explicitAction = cleanText(element.getAttribute('data-ym-click'))
  const pathname = typeof window === 'undefined'
    ? undefined
    : `${window.location.pathname}${window.location.search}`

  return compactParams({
    action: explicitAction ?? label ?? href ?? tag,
    label,
    href,
    tag,
    role,
    inputType,
    id: cleanText(element.id, 80),
    className,
    path: pathname,
  })
}

function handleDocumentClick(event: MouseEvent): void {
  if (!TRACK_ALL_CLICKS || typeof document === 'undefined') return
  if (!(event.target instanceof Element)) return

  const trackable = event.target.closest(CLICK_SELECTOR)
  if (!(trackable instanceof HTMLElement)) return
  if (trackable instanceof HTMLButtonElement && trackable.disabled) return
  if (trackable instanceof HTMLInputElement && trackable.disabled) return

  reachGoal('ui_click', extractClickPayload(trackable))
}

export function initYm(): void {
  if (!isEnabled() || initialized || typeof window === 'undefined') return
  initialized = true

  const w = window as Window & { ym?: YmFn }
  if (!w.ym) {
    const stub = function (this: unknown, ...args: unknown[]) {
      ;(stub.a = stub.a || []).push(args)
    } as YmFn
    stub.l = Date.now()
    w.ym = stub

    const script = document.createElement('script')
    script.async = true
    script.src = SCRIPT_SRC
    const first = document.getElementsByTagName('script')[0]
    if (first?.parentNode) {
      first.parentNode.insertBefore(script, first)
    } else {
      document.head.appendChild(script)
    }
  }

  w.ym!(COUNTER_ID, 'init', {
    defer: true,
    clickmap: true,
    trackLinks: true,
    accurateTrackBounce: true,
    webvisor: WEBVISOR,
  })
}

export function trackPageview(url: string, title?: string): void {
  if (!isEnabled() || typeof window === 'undefined' || !window.ym) return
  window.ym(COUNTER_ID, 'hit', url, title ? { title } : {})
}

export function setYmUserId(userId: string | number | null | undefined): void {
  if (!isEnabled() || typeof window === 'undefined' || !window.ym) return
  if (userId === null || userId === undefined || userId === '') return
  window.ym(COUNTER_ID, 'setUserID', String(userId))
}

export function reachGoal(target: string, params?: Record<string, unknown>): void {
  if (!isEnabled() || typeof window === 'undefined' || !window.ym) return
  window.ym(COUNTER_ID, 'reachGoal', target, params)
}

// --- Продуктовые события нового дашборда (префикс mtr_) ---------------------
// Отдельная линия целей, не пересекается со старыми auth/pwa/page целями.
// reachMtrGoal автоматически навешивает базовые параметры (screen,
// contract_number, is_pwa, theme), чтобы каждый callsite не собирал их вручную.

export type MtrScreen =
  | 'dashboard'
  | 'invoices'
  | 'invoice_payment'
  | 'profile'
  | 'autopay'
  | 'support'
  | 'ticket'

// contract_number в base-params. user↔contract 1:1 на протяжении сессии
// (тот же инвариант, что и у setYmVisitParams в useAuthInit). Кешируем здесь,
// чтобы reachMtrGoal не зависел от Redux store и работал из любого callsite'а.
let currentContractNumber: string | undefined

export function setMtrContractNumber(contract: string | number | null | undefined): void {
  if (contract === null || contract === undefined || contract === '') {
    currentContractNumber = undefined
    return
  }
  const digits = String(contract).replace(/\D/g, '')
  currentContractNumber = digits.length > 0 ? digits : undefined
}

function detectMtrTheme(): 'light' | 'dark' {
  if (typeof document === 'undefined') return 'light'
  return document.documentElement.classList.contains('dark') ? 'dark' : 'light'
}

export function reachMtrGoal(
  goal: string,
  screen: MtrScreen,
  extra?: Record<string, unknown>,
): void {
  if (!isEnabled() || typeof window === 'undefined' || !window.ym) return
  const ctx = getPwaContext()
  const params = compactParams({
    screen,
    contract_number: currentContractNumber,
    is_pwa: ctx.standalone,
    theme: detectMtrTheme(),
    ...extra,
  })
  window.ym(COUNTER_ID, 'reachGoal', goal, params)
}

// userParams — visitor-scoped, lookup в Webvisor "Параметры посетителя".
// ВАЖНО: ретроактивен. Перезатирает userParams на всей истории user.id.
export function setYmUserParams(params: Record<string, unknown>): void {
  if (!isEnabled() || typeof window === 'undefined' || !window.ym) return
  window.ym(COUNTER_ID, 'userParams', params)
}

// Visit params — frozen per-visit, главный канал для reliable webvisor filter
// и historical reports. Обёртка { params: payload } обязательна per
// https://yandex.com/support/metrica/objects/params-method.html
export function setYmVisitParams(payload: Record<string, unknown>): void {
  if (!isEnabled() || typeof window === 'undefined' || !window.ym) return
  window.ym(COUNTER_ID, 'params', { params: payload })
}

// Logout identity tombstone — best-effort. Metrika не имеет документированного
// "logout" для JS тега. Мы ставим tombstone в userParams (пустая строка),
// чтобы sidebar "Параметры посетителя" для новых визитов не нёс старый
// договор. setUserID('') намеренно не шлём — поведение на пустую строку
// не документировано. Для строгой shared-device изоляции — window.location.reload()
// после logout (не делаем в V1).
export function clearYmIdentity(): void {
  if (!isEnabled() || typeof window === 'undefined' || !window.ym) return
  window.ym(COUNTER_ID, 'userParams', { account: { contract_number: '' } })
}

/**
 * reachGoal для PWA-событий. Автоматически прицепляет `platform` и
 * `standalone` (и `iosVersion` на iOS) из getPwaContext(), чтобы потом
 * строить разбивку в Метрике через paramsLevel1/2 без ручного сбора
 * platform в каждом callsite'е.
 *
 * Пример:
 *   reachPwaGoal('pwa_install_clicked')
 *   reachPwaGoal('pwa_push_subscribe_failed', { status: 503 })
 */
export function reachPwaGoal(target: string, extra?: Record<string, unknown>): void {
  if (!isEnabled() || typeof window === 'undefined' || !window.ym) return
  const ctx = getPwaContext()
  const params: Record<string, unknown> = {
    platform: ctx.platform,
    standalone: ctx.standalone,
    ...(ctx.iosVersion ? { iosVersion: ctx.iosVersion } : {}),
    ...extra,
  }
  window.ym(COUNTER_ID, 'reachGoal', target, params)
}

export function bindYmClickTracking(): (() => void) | undefined {
  if (!isEnabled() || !TRACK_ALL_CLICKS || typeof document === 'undefined') return undefined

  document.addEventListener('click', handleDocumentClick, true)
  return () => {
    document.removeEventListener('click', handleDocumentClick, true)
  }
}

export const ymCounterId = COUNTER_ID
export const ymEnabled = isEnabled
